import isempty from 'lodash/isEmpty';
import {Map} from 'immutable';
import {reportError, reportSuccess} from '@homeaway/react-error-reporter';

import {pricingGraphql} from '../api/pricingGraphQl';
import {log as logToServer} from '../api/logger';
import edapActions from '../actions/edapActions';
import listingActions from '../actions/listingActions';
import cancellationPolicyActions from '../actions/cancellationPolicyActions';
import {priceErrorMap, types} from './errorEvents';

import {priceSummaryGraphql} from '../api/priceSummaryGraphQl';
import {ACCESS_TOKEN_MISSING_ERROR_CODE} from './ajaxHelper';

function createDefaultPricingKey(tripData, systemData) {
    return new Map({
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        adults: tripData.adults,
        children: tripData.children,
        pets: tripData.pets,
        currency: systemData.currency
    });
}

const handleOriginalErrorCode = (graphqlPricingResult) => {
    if (!graphqlPricingResult) {
        graphqlPricingResult = {originalErrorCode: 500};
    }
    if (!graphqlPricingResult.originalErrorCode) {
        // for some unexpected graphql errors (eg. Cannot return null for non-nullable field)
        if (graphqlPricingResult.errors && graphqlPricingResult.errors.length) {
            graphqlPricingResult.originalErrorCode = 500;
        }
        if (graphqlPricingResult.errorCode) {
            graphqlPricingResult.originalErrorCode = graphqlPricingResult.errorCode;
        }
    }
    return graphqlPricingResult;
};

function getError(checkoutError) {
    const errorCode = checkoutError ? checkoutError.errorCode : 'UNDEFINED';

    if (errorCode) {
        return priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;
    }

    return null;
}

/* eslint-disable complexity */
function handleError(store, pageLoadPriceCall = false, checkoutError, quoteEventParams) {
    const error = getError(checkoutError);

    if (error) {
        const validDates = error.type !== types.DATES;
        const validGuests = error.type !== types.GUESTS;

        store.dispatch(
            listingActions.datesAreValid({
                datesValid: validDates
            })
        );

        store.dispatch(listingActions.guestsAreValid(validGuests));

        const {listingReducer, tripDetailsReducer, systemReducer} = store.getState();
        if (!validDates || !validGuests) {
            const defaultPricingKey = createDefaultPricingKey(tripDetailsReducer, systemReducer);
            store.dispatch(listingActions.pricingInfoReset(defaultPricingKey));
        }

        let newQuoteEventParams;
        if (quoteEventParams) {
            // eslint-disable-next-line camelcase
            const {eventstartdate: date_start, eventenddate: date_end, ...rest} = quoteEventParams;
            newQuoteEventParams = {date_start, date_end, ...rest};
        } else {
            newQuoteEventParams = quoteEventParams;
        }
        store.dispatch(edapActions.triggerEdapEvent('quote.failure', quoteEventParams));
        store.dispatch(
            edapActions.triggerEdapEvent('price_quote_request.failed', newQuoteEventParams)
        );

        store.dispatch(cancellationPolicyActions.failedOnQuoteLoad());

        if (checkoutError && checkoutError.errorCode === ACCESS_TOKEN_MISSING_ERROR_CODE) {
            reportError({name: 'quote request rejected'});
            reportError({name: 'price_quote_request rejected'});
        } else {
            reportError({name: 'quote failure'});
            reportError({name: 'price_quote_request failed'});
        }

        const graphqlRequestMarker =
            checkoutError && checkoutError.graphqlRequestMarker
                ? checkoutError.graphqlRequestMarker
                : null;

        logToServer({
            message: 'quote.attempt',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });

        logToServer({
            message: 'price_quote_request.initiated',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });
    }
}
/* eslint-enable complexity */

function getPriceSummaryObjectByType(priceSummary, priceSummarySecondary, type) {
    if (priceSummary && priceSummary.priceTypeId === type) {
        return {
            amount: priceSummary.formattedAmount,
            label: priceSummary.pricePeriodDescription,
            isPrimary: true
        };
    }
    if (priceSummarySecondary && priceSummarySecondary.priceTypeId === type) {
        return {
            amount: priceSummarySecondary.formattedAmount,
            label: priceSummarySecondary.pricePeriodDescription,
            isPrimary: false
        };
    }
    return {amount: '', label: ''};
}

function getPriceSummaryAmountByType(priceSummary, priceSummarySecondary, type) {
    if (priceSummary && priceSummary.priceTypeId === type) {
        return priceSummary.formattedAmount;
    }
    if (priceSummarySecondary && priceSummarySecondary.priceTypeId === type) {
        return priceSummarySecondary.formattedAmount;
    }
    return '';
}

/* eslint-disable complexity */
function handleGraphQlResult(
    store,
    pageLoadPriceCall,
    priceDetails,
    arrival,
    departure,
    adults,
    children,
    currency,
    pets,
    clientPriceFetchTime,
    displayLocale,
    defaultadults
) {
    const {listingReducer} = store.getState();

    const priceDetailsData = priceDetails.priceDetails || {};
    const priceDetailsError = priceDetails.originalErrorCode;
    const quoteEventParams = priceDetailsData.edapQuoteSuccess
        ? JSON.parse(priceDetailsData.edapQuoteSuccess)
        : {};
    quoteEventParams.defaultadults = defaultadults;
    if (priceDetailsError || isempty(priceDetailsData)) {
        const checkoutError = {
            errorCode: priceDetailsError || 'API_BROKEN'
        };
        if (priceDetails.graphqlRequestMarker) {
            checkoutError.graphqlRequestMarker = priceDetails.graphqlRequestMarker;
        }

        const quoteFailureEventParams = {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            eventrequestmarker: priceDetails.graphqlRequestMarker,
            eventerrorcode: checkoutError.errorCode.toString()
        };
        handleError(store, pageLoadPriceCall, checkoutError, quoteFailureEventParams);
    } else {
        store.dispatch(
            cancellationPolicyActions.updateCancellationPolicy(priceDetailsData.cancellationPolicy)
        );
        store.dispatch(edapActions.triggerEdapEvent('quote.success', quoteEventParams));
        store.dispatch(
            edapActions.triggerEdapEvent('price_quote_request.succeeded', quoteEventParams)
        );

        reportSuccess({name: 'quote success'});
        reportSuccess({name: 'price_quote_request succeeded'});

        logToServer({
            message: 'quote.attempt',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });

        logToServer({
            message: 'price_quote_request.initiated',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
const pricingHelper = async ({
    store,
    listingId = '',
    displayLocale = '',
    arrival = '',
    departure = '',
    adults = 0,
    children = 0,
    pets = 0,
    defaultadults = 'false',
    currency = '',
    loadingRate = () => {},
    loadedRate = () => {},
    pageLoadPriceCall = false,
    unitApiUrl = ''
}) => {
    reportSuccess({name: 'quote attempt'});
    reportSuccess({name: 'price_quote_request initiated'});

    store.dispatch(loadingRate(true));
    store.dispatch(
        edapActions.triggerEdapEvent('quote.attempt', {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );
    store.dispatch(
        edapActions.triggerEdapEvent('price_quote_request.initiated', {
            date_start: arrival,
            date_end: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );

    const startPricingCall = Date.now();

    try {
        const graphqlPricingArgs = {
            unit: unitApiUrl,
            arrivalDate: arrival,
            departureDate: departure,
            adults,
            children,
            pets,
            listingRef: listingId,
            currency
        };
        const graphqlPriceSummaryArgs = {
            unitId: unitApiUrl,
            arrivalDate: arrival,
            departureDate: departure,
            listingUrl: listingId,
            adults,
            children,
            petIncluded: pets,
            currency
        };

        const [graphqlPricingResult, graphqlPriceSummaryResult] = await Promise.all([
            pricingGraphql(graphqlPricingArgs, displayLocale, store),
            priceSummaryGraphql(graphqlPriceSummaryArgs, displayLocale)
        ]);

        const pricingKey = {
            arrival,
            departure,
            adults,
            children,
            currency,
            pets
        };
        let clientPriceFetchTime = graphqlPricingResult.endTime - startPricingCall;

        handleOriginalErrorCode(graphqlPricingResult);

        handleGraphQlResult(
            store,
            pageLoadPriceCall,
            graphqlPricingResult,
            arrival,
            departure,
            adults,
            children,
            currency,
            pets,
            clientPriceFetchTime,
            displayLocale,
            defaultadults
        );
        // Load price details

        store.dispatch(
            loadedRate(
                {},
                graphqlPricingResult,
                pricingKey,
                graphqlPriceSummaryResult ? graphqlPriceSummaryResult.listing : {}
            )
        );
    } catch (err) {
        // The only exception that should be caught here is for the primary pricing call.
        // The secondary compared call was wrapped in the 'whoCaresIfFailedPromise' Promise.
        const checkoutError = {
            id: 'UNKNOWN',
            errorCode: 'INTERNAL_ERROR',
            message: `Pricing call error: ${err}`
        };

        handleError(store, pageLoadPriceCall, checkoutError, {});

        // If graphql was primary, fill in the empty structure's error code.
        store.dispatch(loadedRate({[listingId]: {checkoutError}}, {}, checkoutError));
        store.dispatch(loadingRate(false));
    }
};
/* eslint-enable complexity */

// used to re-request price summaries when dates are cleared
async function priceSummariesHelper({
    store,
    displayLocale = '',
    listingId = '',
    arrival = '',
    departure = '',
    adults = 0,
    children = 0,
    pets = 0,
    currency = '',
    loadingRate = () => {},
    loadedRate = () => {},
    unitApiUrl = ''
}) {
    store.dispatch(loadingRate(true));
    const graphqlPriceSummaryArgs = {
        unitId: unitApiUrl,
        arrivalDate: arrival,
        departureDate: departure,
        listingUrl: listingId,
        adults,
        children,
        petIncluded: pets,
        currency
    };

    const graphqlPriceSummaryResult = await priceSummaryGraphql(
        graphqlPriceSummaryArgs,
        displayLocale
    );
    const pricingKey = {
        arrival,
        departure,
        adults,
        children,
        currency,
        pets
    };

    store.dispatch(
        loadedRate(
            {},
            {},
            pricingKey,
            graphqlPriceSummaryResult ? graphqlPriceSummaryResult.listing : {}
        )
    );
}

export default pricingHelper;
export {
    getError,
    handleError,
    handleGraphQlResult,
    pricingHelper,
    priceSummariesHelper,
    createDefaultPricingKey,
    getPriceSummaryObjectByType,
    getPriceSummaryAmountByType,
    handleOriginalErrorCode
};
